/* @flow */

import type { Customer } from "shop-state/types";

import React, { useContext, useRef } from "react";
import cn from "classnames";
import { Link, NavLink, useLocation } from "react-router-dom";
import { NavHashLink } from "react-router-hash-link";
import { useTranslate } from "@awardit/react-use-translate";
import { StoreInfoContext } from "entrypoint/shared";
import Wrapper from "components/Wrapper";
import CartCounter from "components/AppHeader/CartCounter";
import { QuoteData } from "data";
import { useData } from "crustate/react";
import SystemMessages from "components/SystemMessages";
import CustomerBalance from "components/CustomerBalance";
import useSessionStorage from "helpers/use-session-storage";
import SearchMenu from "../SearchMenu";
import Drawer from "../Drawer";
import ChevronIcon from "icons/chevron.svg";
import SearchIcon from "icons/search.svg";
import Logo from "../logo.svg";
import Button from "components/Button";

import styles from "./styles.scss";
import { useViewMode } from "context/view-mode";

import { CartMini } from "@crossroads/ui-components";

type Props = {
  className: string,
  customer: ?Customer,
  onCheckout: boolean,
  useBannerStyling: boolean,
  hamburgerOpen: boolean,
};

const AppHeaderLarge = ({
  className = "",
  customer,
  onCheckout,
  useBannerStyling,
  hamburgerOpen,
}: Props): React$Node => {
  const location = useLocation();
  const {
    categories,
    occasions,
  } = useContext(StoreInfoContext);
  const t = useTranslate();
  const [viewMode, setViewMode] = useViewMode();
  const onRedeem = (location ? location.pathname : "").includes("redeem");
  const toggleSearch = () => setViewMode(viewMode === "search" ? "normal" : "search");
  const toggleCategories = () => setViewMode(viewMode === "categories" ? "normal" : "categories");
  const toggleOccasions = () => setViewMode(viewMode === "occasions" ? "normal" : "occasions");
  const toggleCart = () => setViewMode(viewMode === "cart" ? "normal" : "cart");
  const quoteData = useData(QuoteData);
  const activeRedeem = quoteData.data && quoteData.data.retain24validation;
  const [isolationMode] = useSessionStorage("navIsolationMode", "false");
  const navIsolationMode = isolationMode === "true";
  const drawerRef = useRef<?HTMLDivElement>();
  const occasionCategories =
    occasions && occasions.category.children ? occasions.category.children : [];
  const showHeader = true;

  return (
    <header className={cn(
      styles.container
    )}
    >
      <div className={cn(
        styles.block,
        { [styles.onRedeem]: onRedeem },
        className
      )}
      >
        <Wrapper className={styles.wrapper}>
          <div className={styles.left}>
            {navIsolationMode ? (
              <div className={styles.padding}>
                <Logo
                  alt={t("LOGO.ALT")}
                  aria-label={t("LOGO.TITLE")}
                  className={styles.logo}
                />
              </div>
            ) : (
              <Link className={cn(styles.item, styles.padding)} to="/">
                <Logo
                  alt={t("LOGO.ALT")}
                  aria-label={t("LOGO.TITLE")}
                  className={styles.logo}
                  onClick={() => setViewMode("normal")}
                />
              </Link>
            )}

            {!onCheckout && !navIsolationMode &&
              <nav aria-label={t("HEADER.MAIN_NAVIGATION")} className={styles.nav}>
                {!activeRedeem &&
                  <NavLink
                    to="/zupergift"
                    activeClassName={styles.active}
                    className={cn(styles.item, styles.padding)}
                  >
                    {t("HEADER.BUY_ZUPERGIFT")}
                  </NavLink>
                }

                {!activeRedeem &&
                  <NavLink
                    to="/redeem"
                    activeClassName={styles.active}
                    className={cn(styles.item, styles.padding)}
                  >
                    {t("HEADER.REDEEM_GIFTCARD")}
                  </NavLink>
                }

                <NavLink
                  to={t("ROUTES.ALL_GIFTCARDS")}
                  activeClassName={styles.active}
                  className={cn(styles.item, styles.padding)}
                >
                  {t("HEADER.ALL_GIFTCARDS")}
                </NavLink>

                <Button
                  type="button"
                  aria-controls="categoriesDesktop"
                  aria-expanded={viewMode === "categories" ?? "true"}
                  className={
                    cn(
                      styles.item,
                      styles.item__categories,
                      { [styles.active]: viewMode === "categories" },
                      styles.padding
                    )
                  }
                  onClick={toggleCategories}
                >
                  {t("HEADER.CATEGORIES")}
                  <ChevronIcon aria-label={t("ICONS.ARROW")} />
                </Button>

                {viewMode === "categories" && !hamburgerOpen &&
                  <nav id="categoriesDesktop" aria-label={t("HEADER.CATEGORIES")} className={cn(styles.categories, { [styles.bannerHeight]: useBannerStyling })}>
                    <Drawer
                      ref={drawerRef}
                      useFocus
                      setMode={setViewMode}
                      heading={t("HEADER.CATEGORIES")}
                      closeText={t("HEADER.CLOSE_CATEGORIES")}
                      items={categories}
                      onClose={toggleCategories}
                    />
                  </nav>
                }

                {occasionCategories.length > 0 &&
                  <Button
                    type="button"
                    aria-controls="occasionsDesktop"
                    aria-expanded={viewMode === "occasions" ?? "true"}
                    className={
                      cn(
                        styles.item,
                        styles.item__categories,
                        { [styles.active]: viewMode === "occasions" },
                        styles.padding
                      )
                    }
                    onClick={toggleOccasions}
                  >
                    {t("HEADER.OCCASIONS")}
                    <ChevronIcon aria-label={t("ICONS.ARROW")} />
                  </Button>
                }

                {viewMode === "occasions" && !hamburgerOpen && occasionCategories.length > 0 &&
                  <nav id="occasionsDesktop" aria-label={t("HEADER.OCCASIONS")} className={cn(styles.categories, { [styles.bannerHeight]: useBannerStyling })}>
                    <Drawer
                      ref={drawerRef}
                      useFocus
                      setMode={setViewMode}
                      heading={t("HEADER.OCCASIONS")}
                      closeText={t("HEADER.CLOSE_OCCASIONS")}
                      items={occasionCategories}
                      onClose={toggleOccasions}
                    />
                  </nav>
                }

                <NavHashLink
                  smooth
                  to="/#about"
                  className={cn(styles.item, styles.padding, {
                    [styles.active]: `${location.pathname}${location.hash}` === "/#about",
                  })}
                  location={{ pathname: location.pathname + location.hash }}
                >
                  {t("HEADER.ABOUT")}
                </NavHashLink>

                <NavLink
                  to="/faq"
                  activeClassName={styles.active}
                  className={cn(styles.item, styles.padding)}
                >
                  {t("HEADER.HELP")}
                </NavLink>

                <NavLink
                  to="/company"
                  activeClassName={styles.active}
                  className={cn(styles.item, styles.padding)}
                >
                  {t("HEADER.COMPANY")}
                </NavLink>
              </nav>
            }
          </div>

          <div className={styles.right}>
            <div className={styles.utilities}>
              {!onCheckout && !navIsolationMode &&
                <Button
                  type="button"
                  aria-controls="search"
                  aria-expanded={viewMode === "search" ?? "true"}
                  aria-label={viewMode === "search" ? t("HEADER.CLOSE_SEARCH") : t("HEADER.OPEN_SEARCH")}
                  className={
                    cn(styles.item, { [styles.active]: viewMode === "search" }, styles.padding)
                  }
                  onClick={toggleSearch}
                >
                  <SearchIcon aria-label={t("ICONS.MAGNIFIER")} />
                </Button>
              }
              {viewMode === "search" &&
                <div id="search" role="search" aria-label={t("HEADER.SEARCH")} className={cn(styles.search, { [styles.bannerHeight]: useBannerStyling })}>
                  <SearchMenu />
                </div>
              }
              <CartCounter className={cn(styles.item, styles.cartCounter, styles.padding)} />
              <CustomerBalance customer={customer} />
            </div>
          </div>
        </Wrapper>
      </div>

      {viewMode === "cart" &&
        <Wrapper>
          <CartMini
            ctaUrl={t("URL.CHECKOUT_CART")}
            variant="zupergift"
            showHeader={showHeader}
            quoteData={quoteData}
            onClose={toggleCart}
          />
        </Wrapper>
      }

      {viewMode !== "normal" && viewMode !== "language-switcher" &&
        <div className={cn(styles.dim, { [styles.bannerHeight]: useBannerStyling })} onClick={() => setViewMode("normal")} />
      }

      <Wrapper>
        <SystemMessages />
      </Wrapper>
    </header>
  );
};

export default AppHeaderLarge;
